/*
 * This MenuLineItem component is used as a composition method for the rows
 * that are rendered for comboslots, modifiers, and sometimes picker aspects.
 * The composition is to allow different UX patterns to exist with a consistent
 * UI of the name/image/padding etc.
 */
import React from 'react';

import { useIntl } from 'react-intl';

import { ISanityImage } from '@rbi-ctg/menu';

import Details from './details';
import Image from './image';
import ImageContainer from './image-container';
import { Label } from './label';
import { LineItemDescription } from './line-item-description';
import Name from './name';
import OptionContainer from './option-container';
import SelectionContainer from './selection-container';

interface IProps {
  'data-testid'?: string;
  bottomContent?: React.ReactNode;
  disabled?: boolean;
  highlightOnHover?: boolean;
  ignoreLabelClicks?: boolean;
  image?: ISanityImage;
  imageDescription?: string;
  indicator: React.ReactNode;
  name: string;
  selected?: boolean;
  details?: string | React.ReactNode;
}

/** @deprecated Use ProductListItem instead */
const MenuLineItem: React.FC<IProps> = ({
  'data-testid': dataTestId,
  bottomContent,
  disabled,
  highlightOnHover = true,
  ignoreLabelClicks,
  image,
  imageDescription,
  indicator,
  name,
  selected,
  details,
}) => {
  const truthyName = imageDescription || name || '';
  const formattedName = truthyName.toLowerCase().replace(/\s/g, '');
  const { formatMessage } = useIntl();

  /**
   * This component (a memoized callback) is created so that `details` is supposed as both a string (in
   * which case it'll be wrapped in <Details />) and as a React Element if a custom wrapper
   * is required.
   */
  const ItemDetails = React.useCallback(() => {
    return typeof details === 'string' ? (
      <Details data-testid={`modifier-option-details-${formattedName}`}>{details}</Details>
    ) : (
      <>{details}</>
    );
  }, [details, formattedName]);

  return (
    <OptionContainer
      data-testid={dataTestId}
      disabled={disabled}
      highlightOnHover={highlightOnHover}
      selected={selected}
    >
      <Label
        aria-label={truthyName || formatMessage({ id: 'option' })}
        role="option"
        ignoreClicks={ignoreLabelClicks}
      >
        <ImageContainer>
          <Image image={image} alt={truthyName} />
        </ImageContainer>
        <SelectionContainer>
          <LineItemDescription>
            <Name data-testid={`modifier-option-${formattedName}`}>{name}</Name>
            <ItemDetails />
          </LineItemDescription>
          {indicator}
        </SelectionContainer>
      </Label>
      {bottomContent}
    </OptionContainer>
  );
};

export default MenuLineItem;
